import React, { useState, useRef, useEffect } from 'react';
import {
    Menu as MenuIcon,
    Notifications,
    NotificationsNone,
    Search,
    Refresh,
    AccountCircle,
    Logout,
    ExpandMore,
    Circle
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { formatNotificationTitle, formatNotificationMessage } from '../../utils/notificationFormatter';

const formatDateTime = (date, time) => {
    if (!date && !time) return '—';
    try {
        const str = [date, time].filter(Boolean).join('T');
        const d = new Date(str);
        if (Number.isNaN(d.getTime())) return '—';
        return d.toLocaleString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch {
        return '—';
    }
};

const CashierHeader = ({ onMenuToggle, onRefresh, loading, terminal }) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const {
        notifications = [],
        markAsRead,
        unViewed = 0
    } = useNotification();

    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    const [searchFocused, setSearchFocused] = useState(false);
    const notifRef = useRef(null);

    const unreadCount = notifications.filter((n) => !n.msgRead).length;
    const displayCount = unViewed > 0 ? unViewed : unreadCount;

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (notifRef.current && !notifRef.current.contains(e.target)) {
                setShowNotifications(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <header className="relative bg-white/90 backdrop-blur-xl border-b border-slate-200/80 shadow-sm sticky top-0 z-30">
            <div className="absolute inset-0 bg-gradient-to-r from-amber-50/30 via-transparent to-orange-50/30 pointer-events-none" />

            <div className="relative px-4 lg:px-8 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 flex-1">
                        <button
                            type="button"
                            onClick={onMenuToggle}
                            className="p-2.5 hover:bg-amber-50 rounded-xl transition-all duration-300 lg:hidden"
                        >
                            <MenuIcon className="text-slate-700 group-hover:text-amber-600 text-2xl" />
                        </button>

                        <div className={`hidden md:flex items-center flex-1 max-w-lg transition-all duration-300 ${searchFocused ? 'max-w-xl' : ''}`}>
                            <div className="relative w-full group">
                                <div className={`relative flex items-center bg-white/80 backdrop-blur-sm border-2 rounded-2xl transition-all duration-300 ${
                                    searchFocused ? 'border-amber-400 shadow-lg shadow-amber-500/20' : 'border-slate-200/80 group-hover:border-amber-200/60'
                                }`}>
                                    <Search className={`absolute left-4 w-5 h-5 transition-colors duration-300 ${searchFocused ? 'text-amber-500' : 'text-slate-400'}`} />
                                    <input
                                        type="text"
                                        placeholder="Rechercher transactions, clients..."
                                        onFocus={() => setSearchFocused(true)}
                                        onBlur={() => setSearchFocused(false)}
                                        className="w-full pl-12 pr-4 py-3 bg-transparent text-slate-700 placeholder-slate-400 focus:outline-none rounded-2xl text-sm"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center space-x-2 sm:space-x-3">
                        {terminal?.tag && (
                            <div className="hidden sm:flex items-center px-4 py-2.5 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 rounded-2xl shadow-lg shadow-amber-500/25 border border-amber-400/30">
                                <div className="w-2.5 h-2.5 bg-white rounded-full animate-pulse mr-2" />
                                <span className="text-sm font-bold text-white tracking-wide">POS {terminal.tag}</span>
                            </div>
                        )}

                        <button
                            type="button"
                            onClick={onRefresh}
                            disabled={loading}
                            className="p-3 bg-white/80 backdrop-blur-sm hover:bg-amber-50 rounded-xl border border-slate-200/80 hover:border-amber-200/60 transition-all disabled:opacity-50"
                            title="Actualiser"
                        >
                            <Refresh className={`text-slate-600 w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                        </button>

                        {/* Notifications */}
                        <div className="relative" ref={notifRef}>
                            <button
                                type="button"
                                onClick={() => setShowNotifications(!showNotifications)}
                                className="relative p-3 bg-white/80 backdrop-blur-sm hover:bg-amber-50 rounded-xl border border-slate-200/80 hover:border-amber-200/60 transition-all"
                                title="Notifications"
                            >
                                {displayCount > 0 ? (
                                    <Notifications className="text-slate-600 w-5 h-5" />
                                ) : (
                                    <NotificationsNone className="text-slate-500 w-5 h-5" />
                                )}
                                {displayCount > 0 && (
                                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full border-2 border-white">
                                        {displayCount > 99 ? '99+' : displayCount}
                                    </span>
                                )}
                            </button>

                            {showNotifications && (
                                <div className="absolute right-0 mt-2 w-96 max-h-[80vh] bg-white rounded-2xl shadow-xl border border-slate-200/80 overflow-hidden z-50 flex flex-col">
                                    <div className="px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-amber-50/80 to-orange-50/50 flex items-center justify-between">
                                        <h3 className="font-bold text-slate-800">Notifications</h3>
                                        {unreadCount > 0 && (
                                            <span className="text-xs font-medium text-amber-700 bg-amber-100 px-2 py-1 rounded-lg">
                                                {unreadCount} non lue{unreadCount > 1 ? 's' : ''}
                                            </span>
                                        )}
                                    </div>
                                    <div className="overflow-y-auto flex-1">
                                        {notifications.length === 0 ? (
                                            <div className="p-8 text-center text-slate-500">
                                                <NotificationsNone className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                                                <p className="text-sm font-medium">Aucune notification</p>
                                            </div>
                                        ) : (
                                            <ul className="divide-y divide-slate-100">
                                                {notifications.slice(0, 20).map((notif) => (
                                                    <li key={notif.id}>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                if (!notif.msgRead) markAsRead(notif.id);
                                                            }}
                                                            className={`w-full text-left px-4 py-3 transition-colors ${
                                                                notif.msgRead ? 'bg-slate-50/50 hover:bg-slate-100/50' : 'bg-amber-50/50 hover:bg-amber-100/50'
                                                            }`}
                                                        >
                                                            <div className="flex items-start gap-3">
                                                                {!notif.msgRead && (
                                                                    <Circle className="w-2 h-2 text-amber-500 flex-shrink-0 mt-1.5 fill-current" />
                                                                )}
                                                                <div className="flex-1 min-w-0">
                                                                    <p className={`text-sm font-semibold ${notif.msgRead ? 'text-slate-600' : 'text-slate-900'}`}>
                                                                        {formatNotificationTitle(notif)}
                                                                    </p>
                                                                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                                                                        {formatNotificationMessage(notif) || 'Nouvelle activité.'}
                                                                    </p>
                                                                    <p className="text-xs text-slate-400 mt-1">
                                                                        {formatDateTime(notif.date, notif.time)}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                    {notifications.length > 0 && (
                                        <div className="p-2 border-t border-slate-100 bg-slate-50/50">
                                            <button
                                                type="button"
                                                onClick={() => navigate('/cashier')}
                                                className="w-full py-2 text-sm font-medium text-amber-700 hover:text-amber-800"
                                            >
                                                Voir tout
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* User menu */}
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => setShowUserMenu(!showUserMenu)}
                                className="flex items-center gap-2 sm:gap-3 px-3 py-2.5 sm:px-4 bg-white/80 backdrop-blur-sm hover:bg-amber-50 rounded-xl border border-slate-200/80 hover:border-amber-200/60 transition-all"
                            >
                                <div className="w-9 h-9 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                                    <AccountCircle className="text-white text-xl" />
                                </div>
                                <div className="hidden lg:block text-left">
                                    <p className="text-sm font-semibold text-slate-800">{user?.name || 'Utilisateur'}</p>
                                    <p className="text-xs text-slate-500">Caissier</p>
                                </div>
                                <ExpandMore className={`text-slate-400 w-5 h-5 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                            </button>

                            {showUserMenu && (
                                <>
                                    <div className="fixed inset-0 z-10" onClick={() => setShowUserMenu(false)} aria-hidden="true" />
                                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-xl border border-slate-200/80 overflow-hidden z-20">
                                        <div className="p-2">
                                            <button
                                                type="button"
                                                onClick={() => { setShowUserMenu(false); logout(); }}
                                                className="w-full flex items-center gap-3 px-4 py-3 text-slate-700 hover:bg-red-50 hover:text-red-600 rounded-xl transition-colors text-left"
                                            >
                                                <Logout className="text-slate-400 w-5 h-5" />
                                                <span className="font-medium">Déconnexion</span>
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
};

export default CashierHeader;
